import React, { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import {
  Box,
  Button,
  Flex,
  FormProvider,
  Heading,
  Stack,
  Text,
  TextInput,
  Title,
  useForm,
} from '@thirstycamel/ui'
import type {
  ConfirmUserSelfExclusionDto,
  RequestUserSelfExclusionDto,
} from '@thirsty-camel/hump-club/src/modules/user/dto/self-exclusion'
import { normalizeAuMobile } from '../../utils/auMobile.util'
import Input from '../Input'
import FormError from '../FormError'
import { humpclub } from '../../utils/backend'
import { SELF_EXCLUSION_REVIEW_EMAIL } from '../../utils/selfExclusionContent.util'
import { REGEX_MOBILE_NUMBER } from '../../utils/regex'
import { useActions } from '../../store/hooks'
import { useUser } from '../../hooks/useUser'

type RequestSelfExclusionResponse = {
  success: true
  phoneNumber: string
  status: 'PENDING'
  expiresAt: string
}

type ConfirmSelfExclusionResponse = {
  success: true
  phoneNumber: string
  status: 'ACTIVE'
  activatedAt: string
}

export interface SelfExclusionRequestFormProps {
  initialValues?: Partial<RequestUserSelfExclusionDto>
}

type FlowStage = 'request' | 'confirm'
type ConfirmationChannel = 'email' | 'sms'

const normaliseOptionalString = (value?: string) => {
  const trimmedValue = value?.trim()
  return trimmedValue ? trimmedValue : undefined
}

const getConfirmationChannel = (
  payload: Pick<RequestUserSelfExclusionDto, 'email'>,
): ConfirmationChannel => (payload.email ? 'email' : 'sms')

const SELF_EXCLUSION_OTP_TTL_MINUTES = 5
const MAX_SELF_EXCLUSION_OTP_SENDS_PER_SESSION = 3
const OTP_SEND_COUNT_SESSION_PREFIX = 'tc_se_otp_sends:'
const RESEND_SMS_COOLDOWN_SECONDS = 30

function readOtpSendCount(normalizedPhone: string): number {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return 0
  }
  const raw = sessionStorage.getItem(OTP_SEND_COUNT_SESSION_PREFIX + normalizedPhone)
  const n = raw ? parseInt(raw, 10) : 0
  return Number.isFinite(n) && n >= 0 ? n : 0
}

function incrementOtpSendCount(normalizedPhone: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return
  }
  const key = OTP_SEND_COUNT_SESSION_PREFIX + normalizedPhone
  const next = readOtpSendCount(normalizedPhone) + 1
  sessionStorage.setItem(key, String(next))
}

function remainingOtpSendsBeforeNext(normalizedPhone: string): number {
  return Math.max(0, MAX_SELF_EXCLUSION_OTP_SENDS_PER_SESSION - readOtpSendCount(normalizedPhone))
}

export default function SelfExclusionRequestForm({ initialValues }: SelfExclusionRequestFormProps) {
  const router = useRouter()
  const { isLoggedIn, user } = useUser()
  const { logout } = useActions(actions => actions.auth)
  const lockedPhoneNumber = isLoggedIn ? user?.mobilePhone || initialValues?.phoneNumber || '' : ''

  const requestForm = useForm<RequestUserSelfExclusionDto>({
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      email: initialValues?.email || '',
      phoneNumber: lockedPhoneNumber || initialValues?.phoneNumber || '',
    },
  })

  const confirmForm = useForm<ConfirmUserSelfExclusionDto>({
    defaultValues: {
      phoneNumber: lockedPhoneNumber || initialValues?.phoneNumber || '',
      otpCode: '',
    },
  })

  const [stage, setStage] = useState<FlowStage>('request')
  const [smsSendError, setSmsSendError] = useState<unknown>(null)
  const [confirmError, setConfirmError] = useState(null)
  const [requestState, setRequestState] = useState<{
    phoneNumber: string
    expiresAt: string
  } | null>(null)
  const [confirmationChannel, setConfirmationChannel] = useState<ConfirmationChannel>('sms')
  const [isRequestLoading, setRequestLoading] = useState(false)
  const [isResendLoading, setResendLoading] = useState(false)
  const [isConfirmLoading, setConfirmLoading] = useState(false)
  const [resendCooldownLeft, setResendCooldownLeft] = useState(0)

  useEffect(() => {
    const nextInitialValues = {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      email: initialValues?.email || '',
      phoneNumber: lockedPhoneNumber || initialValues?.phoneNumber || '',
    }

    const requestValues = requestForm.getValues()

    // Account pages fetch the logged-in user after the form mounts, so we only hydrate blank fields
    // here instead of resetting the whole form and risking that we wipe any customer input in progress.
    ;(['firstName', 'lastName', 'email', 'phoneNumber'] as const).forEach(fieldName => {
      if (!requestValues[fieldName] && nextInitialValues[fieldName]) {
        requestForm.setValue(fieldName, nextInitialValues[fieldName])
      }
    })

    if (lockedPhoneNumber && requestForm.getValues('phoneNumber') !== lockedPhoneNumber) {
      requestForm.setValue('phoneNumber', lockedPhoneNumber)
    }

    if (lockedPhoneNumber && confirmForm.getValues('phoneNumber') !== lockedPhoneNumber) {
      confirmForm.setValue('phoneNumber', lockedPhoneNumber)
    } else if (!confirmForm.getValues('phoneNumber') && nextInitialValues.phoneNumber) {
      confirmForm.setValue('phoneNumber', nextInitialValues.phoneNumber)
    }
  }, [
    confirmForm,
    initialValues?.email,
    initialValues?.firstName,
    initialValues?.lastName,
    initialValues?.phoneNumber,
    lockedPhoneNumber,
    requestForm,
  ])

  useEffect(() => {
    if (resendCooldownLeft <= 0 || stage !== 'confirm') {
      return
    }
    const id = window.setInterval(() => {
      setResendCooldownLeft(s => Math.max(0, s - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [resendCooldownLeft, stage])

  const postSelfExclusionRequest = useCallback(
    async (payload: RequestUserSelfExclusionDto): Promise<RequestSelfExclusionResponse> => {
      const normalizedPhone = normalizeAuMobile(payload.phoneNumber)
      if (!normalizedPhone) {
        throw new Error('Please enter a valid Australian mobile number.')
      }

      if (readOtpSendCount(normalizedPhone) >= MAX_SELF_EXCLUSION_OTP_SENDS_PER_SESSION) {
        throw new Error(
          `You have used the maximum number of SMS code requests. Contact ${SELF_EXCLUSION_REVIEW_EMAIL} if you need help completing self exclusion.`,
        )
      }

      const response = await humpclub<RequestSelfExclusionResponse>('/self-exclusion/request', {
        method: 'post',
        data: payload,
      })

      incrementOtpSendCount(response.phoneNumber)
      return response
    },
    [],
  )

  const handleRequestSubmit = requestForm.handleSubmit(async values => {
    setSmsSendError(null)
    setRequestLoading(true)

    try {
      const payload: RequestUserSelfExclusionDto = {
        phoneNumber: lockedPhoneNumber || values.phoneNumber,
        firstName: normaliseOptionalString(values.firstName),
        lastName: normaliseOptionalString(values.lastName),
        email: normaliseOptionalString(values.email),
      }

      const response = await postSelfExclusionRequest(payload)
      setConfirmationChannel(getConfirmationChannel(payload))

      setRequestState({
        phoneNumber: response.phoneNumber,
        expiresAt: response.expiresAt,
      })
      confirmForm.setValue('phoneNumber', response.phoneNumber)
      setResendCooldownLeft(RESEND_SMS_COOLDOWN_SECONDS)
      setStage('confirm')
    } catch (error) {
      setSmsSendError(error)
    } finally {
      setRequestLoading(false)
    }
  })

  const handleResendCode = async () => {
    setSmsSendError(null)
    setResendLoading(true)

    try {
      const values = requestForm.getValues()
      const payload: RequestUserSelfExclusionDto = {
        phoneNumber: lockedPhoneNumber || values.phoneNumber,
        firstName: normaliseOptionalString(values.firstName),
        lastName: normaliseOptionalString(values.lastName),
        email: normaliseOptionalString(values.email),
      }

      const response = await postSelfExclusionRequest(payload)
      setConfirmationChannel(getConfirmationChannel(payload))

      setRequestState({
        phoneNumber: response.phoneNumber,
        expiresAt: response.expiresAt,
      })
      confirmForm.setValue('phoneNumber', response.phoneNumber)
      setResendCooldownLeft(RESEND_SMS_COOLDOWN_SECONDS)
    } catch (error) {
      setSmsSendError(error)
    } finally {
      setResendLoading(false)
    }
  }

  const handleConfirmSubmit = confirmForm.handleSubmit(async values => {
    setConfirmError(null)
    setConfirmLoading(true)

    try {
      const response = await humpclub<ConfirmSelfExclusionResponse>('/self-exclusion/confirm', {
        method: 'post',
        data: {
          ...values,
          phoneNumber: requestState?.phoneNumber || values.phoneNumber,
        },
      })

      setRequestState(current => ({
        phoneNumber: response.phoneNumber,
        expiresAt: current?.expiresAt || '',
      }))

      if (isLoggedIn) {
        await router.replace({
          pathname: '/self-exclusion/confirmation',
          query: { source: 'account', confirmationChannel },
        })
        logout()
        return
      }

      await router.replace({
        pathname: '/self-exclusion/confirmation',
        query: { confirmationChannel },
      })
    } catch (error) {
      setConfirmError(error)
    } finally {
      setConfirmLoading(false)
    }
  })

  const resendAttemptsLeft =
    requestState?.phoneNumber != null ? remainingOtpSendsBeforeNext(requestState.phoneNumber) : 0
  const canRetrySms = resendAttemptsLeft > 0 && !isResendLoading

  return (
    <Stack spacing={8}>
      <FormProvider {...requestForm}>
        <Stack as="form" spacing={5} onSubmit={handleRequestSubmit}>
          <Stack spacing={2} textAlign="center" align="center">
            <Title>Request Self Exclusion</Title>
            <Text>
              We are committed to the safety of our customers and promote the responsible
              consumption of alcohol.
            </Text>
            <Text>
              If you have a problem with alcohol, or you believe you may be at risk of developing a
              problem with alcohol, we invite you to self exclude from our services and the products
              we sell online.
            </Text>
          </Stack>

          <Stack spacing={4}>
            <Flex direction={['column', null, 'row']} mx={-2}>
              <Box w={['100%', null, '50%']} px={2} mb={[4, null, 0]}>
                <Input as={TextInput} name="firstName" label="First name" />
              </Box>

              <Box w={['100%', null, '50%']} px={2}>
                <Input as={TextInput} name="lastName" label="Last name" />
              </Box>
            </Flex>

            <Flex direction={['column', null, 'row']} mx={-2}>
              <Box w={['100%', null, '50%']} px={2} mb={[4, null, 0]}>
                <Input
                  as={TextInput}
                  name="email"
                  type="email"
                  label="Email"
                  rules={{
                    validate: value => {
                      if (!value?.trim()) {
                        return true
                      }

                      return /.+\@.+\..+/.test(value.trim())
                        ? true
                        : 'Please enter a valid email address.'
                    },
                  }}
                />
              </Box>

              <Box w={['100%', null, '50%']} px={2}>
                <Input
                  as={TextInput}
                  name="phoneNumber"
                  type="tel"
                  label="Mobile number"
                  isRequired
                  isReadOnly={!!lockedPhoneNumber}
                  rules={{
                    required: 'Please enter your mobile number.',
                    pattern: {
                      value: REGEX_MOBILE_NUMBER,
                      message: 'Please enter a valid Australian mobile number.',
                    },
                  }}
                />
              </Box>
            </Flex>
          </Stack>

          {smsSendError && <FormError variant="left-accent" errors={smsSendError} />}

          {stage === 'request' && (
            <Button
              type="submit"
              variantColor="pink"
              isLoading={isRequestLoading}
              width={['80%', null, '50%']}
              alignSelf="center"
            >
              Send SMS Code
            </Button>
          )}
        </Stack>
      </FormProvider>

      {stage === 'confirm' && (
        <FormProvider {...confirmForm}>
          <Stack as="form" spacing={5} onSubmit={handleConfirmSubmit} align="center">
            <Input
              as={TextInput}
              name="otpCode"
              label="Confirmation code"
              isRequired
              rules={{
                required: 'Please enter your confirmation code.',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'Please enter the 6-digit confirmation code.',
                },
              }}
              width={['100%', null, '50%']}
              mt={3}
            />

            <Text textAlign="center">
              Enter the 6-digit confirmation code sent to your mobile to activate self exclusion.
              The code expires in {SELF_EXCLUSION_OTP_TTL_MINUTES} minutes.
            </Text>

            {canRetrySms && (
              <Text fontSize="sm" color="gray.600" textAlign="center" mt={-2}>
                If you didn&apos;t receive the SMS, press{' '}
                {resendCooldownLeft > 0 ? (
                  <>
                    <Text as="span" textDecoration="underline" color="gray.400">
                      here
                    </Text>
                    {` to retry in ${resendCooldownLeft} second${
                      resendCooldownLeft === 1 ? '' : 's'
                    }`}
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="link"
                      variantColor="pink"
                      size="sm"
                      fontSize="sm"
                      minH="auto"
                      h="auto"
                      p={0}
                      lineHeight="inherit"
                      verticalAlign="baseline"
                      isLoading={isResendLoading}
                      isDisabled={isResendLoading}
                      onClick={() => void handleResendCode()}
                    >
                      here
                    </Button>
                    {' to retry.'}
                  </>
                )}
              </Text>
            )}

            {smsSendError && <FormError variant="left-accent" errors={smsSendError} />}

            {confirmError && <FormError variant="left-accent" errors={confirmError} />}

            {resendAttemptsLeft === 0 && (
              <Text fontSize="sm">
                Maximum SMS attempts reached. Contact <b>{SELF_EXCLUSION_REVIEW_EMAIL}</b> if you
                need help completing self exclusion.
              </Text>
            )}

            <Button
              type="submit"
              variantColor="pink"
              isLoading={isConfirmLoading}
              width={['80%', null, '50%']}
              alignSelf="center"
            >
              Confirm self exclusion
            </Button>
          </Stack>
        </FormProvider>
      )}
    </Stack>
  )
}
